import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useActualIncidentId } from './hooks/commonHooks';
import type {
  CellContext,
  ColumnDef,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  useIncidentActions,
  useUpdateIncidentAction,
} from './hooks/incidentActionHooks';
import { useOrgActions, useUpdateOrgAction } from './hooks/orgActionHooks';
import PratusTable from '../DesignSystems/PratusTable/PratusTable';
import { useAorText } from 'utils/aorTerminology';
import Loading from '../Loading/Loading';
import { IapScope, INCIDENT } from './ICSConstants';
import {
  actionClassificationAbbrev,
  actionClassificationSegments,
  normalizeActionClassification,
} from './actionClassification';
import ActionRowMenu from './ActionRowMenu';
import { ActionEntity } from './hooks/useScopedActionForm';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import type { UscgMapContextType } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { getActionCoordinates } from './utils/actionGeolocation';
import { getActionMapId } from './utils/actionMapId';
import { formatActionTask } from './utils/actionTask';
import { SubTaskCompletionRing } from './SubTaskCompletionRing';
import { ActionMapButton } from './ActionMapButton';
import { useActionFieldEdit } from './hooks/useActionFieldEdit';
import { useActionAssigneeDirectory } from './hooks/useActionAssigneeDirectory';
import { AssigneeCell } from './actionsTable/AssigneeCell';
import { StatusCell } from './actionsTable/StatusCell';
import { PriorityCell } from './actionsTable/PriorityCell';
import { DueDateCell } from './actionsTable/DueDateCell';
import {
  actionPriorityRank,
  resolveActionAssignee,
} from './actionsTable/actionEditModel';
import { blockingCount } from './utils/actionDependencies';
import { useActionColumnVisibility } from './actionsTable/useActionColumnVisibility';

function stopRowClick(event: React.MouseEvent) {
  event.stopPropagation();
}

function actionLocationLabel(action: ActionEntity): string {
  const geolocation = (
    action as ActionEntity & {
      geolocation?: { address?: string; name?: string } | null;
    }
  ).geolocation;
  return (
    geolocation?.address ||
    geolocation?.name ||
    (geolocation ? 'Map location set' : '—')
  );
}

type ActionsTableProps = {
  scope?: IapScope;
  onEditAction: (action: any) => void;
  /** When set, View opens this callback instead of navigating to ActionView. */
  onViewAction?: (action: any) => void;
  /** When set, load incident actions for this id instead of route/Redux. */
  incidentIdOverride?: string | null;
  canEdit?: boolean;
  /** Controlled selection so the workspace can drive bulk operations. */
  selectedRows?: RowSelectionState;
  onSelectedRowsChange?: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;
  /** When set, the workspace already applied list filters. */
  filteredActions?: ActionEntity[];
  columnVisibility?: VisibilityState;
  hasActiveFilters?: boolean;
  /** Show the selection checkboxes. */
  selectable?: boolean;
  highlightedActionIds?: ReadonlySet<string>;
  /** Shown after the task name, e.g. a "Generated" marker. */
  renderNameBadge?: (action: ActionEntity) => React.ReactNode;
  emptyMessage?: string;
  /**
   * Do not read or publish map state. A second table elsewhere on the page
   * would otherwise replace the tasks the map is showing.
   */
  detachFromMap?: boolean;
};

/**
 * Stable stand-in when this table is rendered off the map, as in the roster
 * modal.
 */
const CLOSED_MAP: Pick<
  UscgMapContextType,
  | 'isMapVisible'
  | 'hiddenActionIds'
  | 'toggleActionHiddenOnMap'
  | 'setVisibleActionIds'
  | 'setSelectedActionIds'
  | 'focusedActionId'
  | 'setFocusedActionId'
  | 'actionAreaFilter'
> = {
  isMapVisible: false,
  hiddenActionIds: [],
  toggleActionHiddenOnMap: () => undefined,
  setVisibleActionIds: () => undefined,
  setSelectedActionIds: () => undefined,
  focusedActionId: null,
  setFocusedActionId: () => undefined,
  actionAreaFilter: null,
};

const ActionsTable: React.FC<ActionsTableProps> = ({
  scope = INCIDENT,
  onEditAction,
  onViewAction,
  incidentIdOverride,
  canEdit = true,
  selectedRows: selectedRowsProp,
  onSelectedRowsChange,
  filteredActions,
  columnVisibility: columnVisibilityProp,
  hasActiveFilters = false,
  selectable = true,
  highlightedActionIds,
  renderNameBadge,
  emptyMessage,
  detachFromMap = false,
}) => {
  const aorText = useAorText();
  const isIncident = scope === INCIDENT;
  const incidentQuery = useIncidentActions(scope, incidentIdOverride);
  const orgQuery = useOrgActions(scope);
  const { data: actions, isLoading } = isIncident ? incidentQuery : orgQuery;
  const { mutateAsync: updateIncidentAction } =
    useUpdateIncidentAction(incidentIdOverride);
  const { mutateAsync: updateOrgAction } = useUpdateOrgAction();
  const { editAction } = useActionFieldEdit(scope, incidentIdOverride);
  const directory = useActionAssigneeDirectory(scope, incidentIdOverride);

  const navigate = useNavigate();
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const mapContext = useOptionalUscgMapContext();
  const {
    isMapVisible,
    hiddenActionIds,
    toggleActionHiddenOnMap,
    setVisibleActionIds,
    setSelectedActionIds,
    focusedActionId,
    setFocusedActionId,
    actionAreaFilter,
  } = (detachFromMap ? null : mapContext) ?? CLOSED_MAP;
  const fallbackColumns = useActionColumnVisibility(scope, isMapVisible);
  const columnVisibility =
    columnVisibilityProp ?? fallbackColumns.columnVisibility;

  const deleteAction = useCallback(
    async (row: ActionEntity) => {
      if (isIncident) {
        await updateIncidentAction({ ...row, archived: true } as any);
      } else {
        await updateOrgAction({ ...row, archived: true } as any);
      }
    },
    [isIncident, updateIncidentAction, updateOrgAction]
  );

  const handleViewAction = useCallback(
    (row: any) => {
      if (onViewAction) {
        onViewAction(row);
        return;
      }
      if (!incidentId) return;
      navigate(`/iap/${incidentId}/actions/${row.action_id || row.id}`);
    },
    [onViewAction, incidentId, navigate]
  );

  const editField = useCallback(
    (action: ActionEntity) => (patch: Record<string, unknown>) => {
      void editAction(action, patch);
    },
    [editAction]
  );

  const activeActions = useMemo(
    () => actions?.filter((action) => !action.archived) ?? [],
    [actions]
  );
  const tableData = filteredActions ?? activeActions;
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>(
    {}
  );
  const rowSelection = selectedRowsProp ?? internalSelection;
  const setRowSelection = onSelectedRowsChange ?? setInternalSelection;
  const selectedCount = useMemo(
    () =>
      tableData.reduce(
        (count, action) => count + (rowSelection[String(action.id)] ? 1 : 0),
        0
      ),
    [rowSelection, tableData]
  );
  const allRowsSelected =
    tableData.length > 0 && selectedCount === tableData.length;
  const someRowsSelected = selectedCount > 0 && !allRowsSelected;
  const getActionRowId = useCallback((row: ActionEntity) => String(row.id), []);

  const columns: ColumnDef<ActionEntity, any>[] = useMemo(() => {
    const selectCol: ColumnDef<ActionEntity, any> = {
      id: '__select',
      enableSorting: false,
      header: () => (
        <Checkbox
          aria-label="Select all tasks"
          checked={allRowsSelected}
          indeterminate={someRowsSelected}
          disabled={tableData.length === 0}
          onCheckedChange={() => {
            setRowSelection((prev) => {
              const ids = tableData.map((action) => String(action.id));
              const allOn =
                ids.length > 0 && ids.every((id) => Boolean(prev[id]));
              if (allOn) return {};
              return Object.fromEntries(ids.map((id) => [id, true]));
            });
          }}
        />
      ),
      cell: ({ row }: CellContext<ActionEntity, any>) => (
        <div onClick={stopRowClick}>
          <Checkbox
            aria-label={`Select ${row.original.name || 'task'}`}
            checked={Boolean(rowSelection[String(row.original.id)])}
            onCheckedChange={() =>
              setRowSelection((prev) => {
                const id = String(row.original.id);
                const next = { ...prev };
                if (next[id]) delete next[id];
                else next[id] = true;
                return next;
              })
            }
          />
        </div>
      ),
      meta: { customSize: true },
      size: 50,
    };

    const idCol: ColumnDef<ActionEntity, any> = {
      id: 'display_id',
      accessorKey: 'display_id',
      header: 'ID',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="tw:font-mono tw:text-xs tw:text-foreground">
          {(row.original as ActionEntity & { display_id?: string })
            .display_id || '—'}
        </span>
      ),
      meta: { cellTitle: 'ID', headerTitle: 'ID' },
      size: 88,
    };

    const aorCol: ColumnDef<ActionEntity, any> = {
      id: 'aor',
      accessorFn: (action) =>
        (action as ActionEntity & { aor_name?: string }).aor_name || '',
      header: aorText('AOR'),
      enableSorting: true,
      cell: ({ row }) => (
        <span className="tw:text-foreground">
          {(row.original as ActionEntity & { aor_name?: string }).aor_name ||
            '—'}
        </span>
      ),
      meta: { cellTitle: aorText('AOR'), headerTitle: aorText('AOR') },
    };

    const workspaceCol: ColumnDef<ActionEntity, any> = {
      id: 'workspace',
      accessorFn: (action) =>
        (
          action as ActionEntity & {
            workspace_name?: string;
            incident_id?: string;
          }
        ).workspace_name ||
        (action as ActionEntity & { incident_id?: string }).incident_id ||
        '',
      header: 'Workspace',
      enableSorting: true,
      cell: ({ row }) => {
        const action = row.original as ActionEntity & {
          workspace_name?: string;
          incident_id?: string;
        };
        return (
          <span className="tw:text-foreground">
            {action.workspace_name || action.incident_id || '—'}
          </span>
        );
      },
      meta: { cellTitle: 'Workspace', headerTitle: 'Workspace' },
    };

    const taskCol: ColumnDef<ActionEntity, any> = {
      id: 'name',
      accessorKey: 'name',
      header: 'Task',
      enableSorting: true,
      cell: ({ row }) => (
        <>
          <strong
            id={
              detachFromMap
                ? undefined
                : `action-row-${getActionMapId(row.original)}`
            }
            className="tw:text-foreground"
          >
            {row.original.name}
          </strong>
          {renderNameBadge ? renderNameBadge(row.original) : null}
        </>
      ),
      meta: { cellTitle: 'Task', headerTitle: 'Task' },
    };

    const subTaskCompletionCol: ColumnDef<ActionEntity, any> = {
      id: 'subtask_completion',
      header: 'Sub-Task Completion',
      enableSorting: false,
      cell: ({ row }) => (
        <SubTaskCompletionRing checklist={row.original.checklist} />
      ),
      meta: {
        cellTitle: 'Sub-Task Completion',
        headerTitle: 'Sub-Task Completion',
      },
    };

    const descriptionCol: ColumnDef<ActionEntity, any> = {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="tw:text-foreground">
          {row.original.description || '—'}
        </span>
      ),
      meta: { cellTitle: 'Description', headerTitle: 'Description' },
    };

    const assigneeCol: ColumnDef<ActionEntity, any> = {
      id: 'assignee',
      header: () => <span data-tour="ics201-action-assignee">Assignee</span>,
      enableSorting: true,
      // Sorts on the resolved name, so team and position assignments sort with
      // people rather than clumping under a blank `poc_user_name`.
      accessorFn: (action) => resolveActionAssignee(action, directory).label,
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <AssigneeCell
            action={row.original}
            directory={directory}
            onChange={editField(row.original)}
            disabled={!canEdit}
          />
        </div>
      ),
      meta: {
        customSize: true,
        cellTitle: 'Assignee',
        headerTitle: 'Assignee',
      },
      size: 170,
    };

    const priorityCol: ColumnDef<ActionEntity, any> = {
      id: 'priority',
      header: 'Priority',
      enableSorting: true,
      accessorFn: (action) => actionPriorityRank(action.priority),
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <PriorityCell
            action={row.original}
            onChange={editField(row.original)}
            disabled={!canEdit}
          />
        </div>
      ),
      meta: {
        customSize: true,
        cellTitle: 'Priority',
        headerTitle: 'Priority',
      },
      size: 110,
    };

    // Incident only: it decides whether a task prints on the ICS-233 or lands
    // on the ICS-215, and org actions reach neither form.
    const classificationCol: ColumnDef<ActionEntity, any> = {
      id: 'classification',
      header: 'Class',
      enableSorting: true,
      accessorFn: (action) =>
        normalizeActionClassification((action as any).classification),
      cell: ({ row }) => {
        const value = (row.original as any).classification;
        const segment = actionClassificationSegments.find(
          (option) => option.value === normalizeActionClassification(value)
        );
        return (
          <span
            className="tw:inline-flex tw:items-center tw:rounded tw:border tw:border-border tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:font-semibold tw:text-muted-foreground"
            title={segment?.tooltip}
          >
            {actionClassificationAbbrev(value)}
          </span>
        );
      },
      meta: {
        customSize: true,
        align: 'center',
        headerAlign: 'center',
        cellTitle: 'Classification',
        headerTitle: 'Classification',
      },
      size: 80,
    };

    const briefedCol: ColumnDef<ActionEntity, any> = {
      id: 'briefed_poc',
      accessorKey: 'briefed_poc',
      header: 'Briefed POC',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.briefed_poc ? (
          <span className="text-success fw-bold">✓</span>
        ) : (
          ''
        ),
      meta: {
        customSize: true,
        align: 'center',
        headerAlign: 'center',
        cellTitle: 'Briefed POC',
        headerTitle: 'Briefed POC',
      },
      size: 100,
    };

    const statusCol: ColumnDef<ActionEntity, any> = {
      id: 'status',
      accessorKey: 'status',
      header: () => <span data-tour="ics201-action-status">Status</span>,
      enableSorting: true,
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <StatusCell
            action={row.original}
            onChange={editField(row.original)}
            disabled={!canEdit}
          />
        </div>
      ),
      meta: { customSize: true, cellTitle: 'Status', headerTitle: 'Status' },
      size: 140,
    };

    const startCol: ColumnDef<ActionEntity, any> = {
      id: 'start_date',
      accessorKey: 'start_date',
      header: 'Start',
      enableSorting: true,
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <DueDateCell
            action={row.original}
            field="start_date"
            onChange={editField(row.original)}
            disabled={!canEdit}
          />
        </div>
      ),
      meta: { customSize: true, cellTitle: 'Start', headerTitle: 'Start' },
      size: 150,
    };

    const blockingCol: ColumnDef<ActionEntity, any> = {
      id: 'blocking',
      header: 'Blocking',
      enableSorting: true,
      accessorFn: (action) =>
        blockingCount(action.action_id, activeActions as ActionEntity[]),
      cell: ({ row }) => {
        const count = blockingCount(
          row.original.action_id,
          activeActions as ActionEntity[]
        );
        return count > 0 ? (
          <Badge variant="secondary">
            {count} {count === 1 ? 'task' : 'tasks'}
          </Badge>
        ) : (
          '—'
        );
      },
      meta: {
        customSize: true,
        cellTitle: 'Blocking',
        headerTitle: 'Blocking',
      },
      size: 110,
    };

    const dueCol: ColumnDef<ActionEntity, any> = {
      id: 'target_date',
      accessorKey: 'target_date',
      header: 'Due',
      enableSorting: true,
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <DueDateCell
            action={row.original}
            field="target_date"
            onChange={editField(row.original)}
            disabled={!canEdit}
          />
        </div>
      ),
      meta: { customSize: true, cellTitle: 'Due', headerTitle: 'Due' },
      size: 150,
    };

    const locationCol: ColumnDef<ActionEntity, any> = {
      id: 'location',
      header: 'Location',
      enableSorting: true,
      accessorFn: (action) => actionLocationLabel(action),
      cell: ({ row }) => (
        <span className="tw:text-foreground">
          {actionLocationLabel(row.original)}
        </span>
      ),
      meta: { cellTitle: 'Location', headerTitle: 'Location' },
      size: 180,
    };

    const mapCol: ColumnDef<ActionEntity, any> = {
      id: 'map',
      header: 'Map',
      enableSorting: false,
      cell: ({ row }) => {
        const action = row.original;
        const coords = getActionCoordinates(
          (action as { geolocation?: unknown }).geolocation
        );
        const actionId = getActionMapId(action);
        const shownOnMap = !hiddenActionIds.includes(actionId);
        return (
          <div
            className="d-flex align-items-center justify-content-center gap-1"
            onClick={(event) => event.stopPropagation()}
          >
            {isMapVisible && coords && (
              <Checkbox
                className="tw:mt-0"
                checked={shownOnMap}
                onCheckedChange={() => toggleActionHiddenOnMap(actionId)}
                aria-label={
                  shownOnMap
                    ? `Hide ${formatActionTask(action.name, action.description)} on map`
                    : `Show ${formatActionTask(action.name, action.description)} on map`
                }
              />
            )}
            <ActionMapButton action={action} />
          </div>
        );
      },
      meta: {
        customSize: true,
        align: 'center',
        headerAlign: 'center',
        cellTitle: 'Map',
        headerTitle: 'Map',
      },
      size: isMapVisible ? 90 : 50,
    };

    const rowMenuCol: ColumnDef<ActionEntity, any> = {
      id: 'row_menu',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div onClick={stopRowClick}>
          <ActionRowMenu
            showView={isIncident}
            canEdit={canEdit}
            onView={
              isIncident ? () => handleViewAction(row.original) : undefined
            }
            onEdit={canEdit ? () => onEditAction(row.original) : undefined}
            onDelete={
              canEdit
                ? () => {
                    void deleteAction(row.original);
                  }
                : undefined
            }
          />
        </div>
      ),
      meta: {
        customSize: true,
        align: 'center',
        headerAlign: 'center',
        cellTitle: 'Actions',
        headerTitle: 'Actions',
      },
      size: 50,
    };

    return [
      ...(selectable ? [selectCol] : []),
      idCol,
      taskCol,
      subTaskCompletionCol,
      descriptionCol,
      aorCol,
      ...(isIncident ? [workspaceCol] : []),
      assigneeCol,
      priorityCol,
      ...(isIncident ? [classificationCol] : []),
      briefedCol,
      statusCol,
      startCol,
      dueCol,
      blockingCol,
      locationCol,
      mapCol,
      ...(isIncident || canEdit ? [rowMenuCol] : []),
    ];
  }, [
    canEdit,
    selectable,
    detachFromMap,
    renderNameBadge,
    aorText,
    deleteAction,
    directory,
    editField,
    handleViewAction,
    hiddenActionIds,
    isIncident,
    isMapVisible,
    onEditAction,
    toggleActionHiddenOnMap,
    activeActions,
    allRowsSelected,
    someRowsSelected,
    tableData,
    rowSelection,
    setRowSelection,
  ]);

  /** What the table shows is what the layer shows, so publish the filtered set. */
  const visibleIds = useMemo(
    () => tableData.map((action) => getActionMapId(action)),
    [tableData]
  );
  useEffect(() => {
    setVisibleActionIds(actionAreaFilter ? visibleIds : null);
  }, [actionAreaFilter, visibleIds, setVisibleActionIds]);
  useEffect(
    () => () => {
      setVisibleActionIds(null);
      setSelectedActionIds([]);
    },
    [setVisibleActionIds, setSelectedActionIds]
  );

  /** Selected rows highlight their graphics; row ids are the entity `id`. */
  useEffect(() => {
    const selectedRowIds = new Set(
      Object.entries(rowSelection)
        .filter(([, selected]) => selected)
        .map(([rowId]) => rowId)
    );
    const nextIds = tableData
      .filter((action) => selectedRowIds.has(String(action.id)))
      .map((action) => getActionMapId(action));
    setSelectedActionIds((prev) => {
      const unchanged =
        prev.length === nextIds.length &&
        prev.every((id, index) => id === nextIds[index]);
      return unchanged ? prev : nextIds;
    });
  }, [rowSelection, tableData, setSelectedActionIds]);

  /** A graphic click asks for its row; scroll to it once, then release. */
  useEffect(() => {
    if (!focusedActionId) return;
    document
      .getElementById(`action-row-${focusedActionId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFocusedActionId(null);
  }, [focusedActionId, setFocusedActionId]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <PratusTable
        columns={columns}
        data={tableData}
        getRowId={getActionRowId}
        columnVisibility={columnVisibility}
        noDataIndication={
          hasActiveFilters
            ? 'No actions match these filters.'
            : actionAreaFilter
              ? 'No actions in this area'
              : (emptyMessage ?? 'No actions found')
        }
        selection={
          selectable
            ? {
                selectedRows: rowSelection,
                onRowSelectionChange: setRowSelection,
              }
            : undefined
        }
        getRowClassName={
          highlightedActionIds && highlightedActionIds.size > 0
            ? (row) =>
                highlightedActionIds.has(String(row.original.action_id))
                  ? 'tw:bg-primary/10'
                  : undefined
            : undefined
        }
        onRowClick={onEditAction}
      />
    </>
  );
};

export default ActionsTable;
